import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { 
  Image, 
  FileSignature, 
  MapPin, 
  Clock, 
  Package,
  CheckCircle,
  ShieldCheck,
  X,
  Check,
  PenTool,
  FileImage
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { http } from '@/lib/http';
import { SafeImage } from '@/components/ui/safe-image';

// Feature toggle for Assignment ID visibility
const SHOW_ASSIGNMENT_ID = import.meta.env.VITE_SHOW_ASSIGNMENT_ID !== 'false';

interface EnhancedProofModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: number;
  cycleId: number;
  operatorId?: number;
  assignmentId?: number; // ✅ NEW: Optional filter to show only specific assignment
  onSuccess: () => void;
  refetch?: () => void;
}

interface PackTaskDetail {
  taskId: number;
  campaignId: number;
  campaignName: string;
  packId: number;
  packName: string;
  taskName: string;
  targetQuantity: number;
  status: string;
  plannedQuantity: number;
  deliveredQuantity: number;
  postingEvent: any;
  proofImages: any[];
  signatures: any[];
  mediaRequirements: Array<{
    formatId: number;
    formatName: string;
    formatType: string;
    quantity: number;
  }>;
}

interface FormatEvidenceDetail {
  formatId: number;
  formatName: string;
  expectedCount: number;
  actualCount?: number;
  status: 'Installed' | 'Not Installed' | 'Partial';
  discrepancyReason?: string;
  completedBy?: 'operator' | 'admin';
  postingEventId?: number;
  proofImageUrl?: string;
}

interface VisitDetails {
  id: number;
  storeId: number;
  operatorId: number;
  completionStatus: string;
  submissionSource: string;
  timestamp: string;
  signatureUrl?: string;
  latitude?: string;
  longitude?: string;
  notes?: string;
}

interface StoreSignatureSummary {
  url: string | null;
  managerName: string | null;
  signoffMethod: 'signature' | 'typed_name' | null;
}

export function EnhancedProofModal({ isOpen, onClose, storeId, cycleId, operatorId, assignmentId, onSuccess, refetch }: EnhancedProofModalProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [storeAssignments, setStoreAssignments] = useState<any[]>([]);
  const [storeInfo, setStoreInfo] = useState<any>(null);
  const [storeSignature, setStoreSignature] = useState<StoreSignatureSummary | null>(null);
  const [formatEvidence, setFormatEvidence] = useState<FormatEvidenceDetail[]>([]);
  const [isFlexiblePack, setIsFlexiblePack] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<{groupId: number, groupName: string} | null>(null);

  // Fetch campaign images using the gallery proxy endpoint
  const [campaignImages, setCampaignImages] = useState<any[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  // Fetch images from gallery proxy when store assignments change
  // FIX: Filter by campaignId and assignmentId to prevent evidence mixing across campaigns
  useEffect(() => {
    const fetchCampaignImages = async () => {
      // When a specific assignment is requested, skip the broad gallery fetch to avoid extra latency
      if (assignmentId) {
        setCampaignImages([]);
        return;
      }

      if (storeAssignments.length === 0) {
        setCampaignImages([]);
        return;
      }

      setImagesLoading(true);
      try {
        // Get unique campaign IDs and assignment IDs from visible assignments
        const campaignIds = [...new Set(storeAssignments.map(a => a.campaignId))];
        const visibleAssignmentIds = new Set(storeAssignments.map(a => a.id));
        const visibleEventIds = new Set(
          storeAssignments.flatMap(a => 
            (a.postingEvents || []).map((pe: any) => pe.id || pe.eventId)
          ).filter(Boolean)
        );
        
        console.log('📡 Fetching campaign images for store', storeId, 'campaigns:', campaignIds, 'assignments:', [...visibleAssignmentIds]);
        
        // Fetch images for this store from gallery proxy
        const response = await http(`/api/gallery-proxy?page=1&limit=50&store=${storeId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch campaign images');
        }
        
        const result = await response.json();
        console.log('📡 Campaign image data (raw):', result.images?.length, 'images');
        
        // Transform images with all available metadata
        const allParsedImages = result.images?.map((img: any) => ({
          id: img.id,
          assetUrl: img.assetUrl,
          secureUrl: img.assetUrl,
          publicId: img.publicId,
          campaignName: img.campaignName || img.campaign?.name,
          storeName: img.storeName || img.store?.name,
          operatorName: img.operatorName || img.operator?.name,
          eventId: img.eventId,
          campaignId: img.campaignId || img.campaign?.id,
          storeId: img.storeId || img.store?.id,
          createdAt: img.createdAt,
          notes: img.notes,
          // Extract assignment/task info from meta or direct fields
          assignmentId: img.assignmentId || img.meta?.assignmentId || img.meta?.taskId || null,
          taskId: img.taskId || img.meta?.taskId || img.meta?.assignmentId || null,
          taskName: img.taskName || img.meta?.taskName || null
        })) || [];
        
        // CRITICAL FIX: Filter images to only show those belonging to visible assignments
        // This prevents evidence from other campaigns appearing in the modal
        const filteredImages = allParsedImages.filter((img: any) => {
          // Must match storeId
          const matchesStore = img.storeId === storeId || img.store?.id === storeId;
          if (!matchesStore) return false;
          
          // If assignmentId prop is provided, we're viewing a specific assignment
          // Filter to only that assignment's evidence via eventId or assignmentId/taskId
          if (assignmentId) {
            // Match by assignmentId/taskId if available
            if (img.assignmentId === assignmentId || img.taskId === assignmentId) {
              return true;
            }
            // Match by eventId if the event belongs to this assignment
            if (img.eventId && visibleEventIds.has(img.eventId)) {
              return true;
            }
            // If no direct match found but campaignId matches visible campaigns, include it
            // This is a fallback for legacy data that may not have assignmentId
            if (campaignIds.includes(img.campaignId)) {
              console.log('📷 Including image via campaignId fallback:', img.campaignId);
              return true;
            }
            return false;
          }
          
          // No specific assignmentId - filter by campaignIds of visible assignments
          // This ensures we only show images from campaigns that have assignments in this view
          return campaignIds.includes(img.campaignId);
        });
        
        console.log('🧩 Campaign parsed images (filtered):', filteredImages.length, 'of', allParsedImages.length, 'images for store', storeId);
        console.log('🧩 Filter criteria - assignmentId:', assignmentId, 'campaignIds:', campaignIds);
        console.log('🧩 Sample image data:', filteredImages[0] ? {
          assetUrl: filteredImages[0].assetUrl,
          assignmentId: filteredImages[0].assignmentId,
          taskId: filteredImages[0].taskId,
          eventId: filteredImages[0].eventId,
          campaignId: filteredImages[0].campaignId
        } : 'No images');
        setCampaignImages(filteredImages);
        
      } catch (error) {
        console.error('Error fetching campaign images:', error);
        setCampaignImages([]);
      } finally {
        setImagesLoading(false);
      }
    };

    fetchCampaignImages();
  }, [storeAssignments, storeId, assignmentId]);

  // Get all proof images from all assignments with deduplication (updated to use gallery proxy data)
  const allProofImages = useMemo(() => {
    // Combine gallery proxy images with legacy assignment images
    const imageMap = new Map<string, any>();
    
    // PRIORITY 1: Use gallery proxy images (most reliable)
    campaignImages.forEach(image => {
      const key = image.assetUrl;
      if (!imageMap.has(key)) {
        imageMap.set(key, {
          secureUrl: image.assetUrl || image.secureUrl, // Map to secureUrl for backward compatibility
          assetUrl: image.assetUrl,
          publicId: image.publicId,
          campaignName: image.campaignName,
          eventId: image.eventId,
          assignmentId: image.assignmentId,
          taskId: image.taskId,
          taskName: image.taskName,
          source: 'gallery-proxy'
        });
      }
    });
    
    // PRIORITY 2: Fallback to assignment embedded images
    storeAssignments.forEach(assignment => {
      // Extract photos from packEvidence structure
      assignment.packEvidence?.proofImages?.forEach((image: any) => {
        // Key on publicId when signing failed, so evidence is still listed
        // (with a placeholder) rather than silently disappearing from review.
        const key = image.secureUrl || image.assetUrl || image.publicId;
        if (key && !imageMap.has(key)) {
          imageMap.set(key, {
            secureUrl: image.secureUrl || image.assetUrl,
            assetUrl: image.assetUrl || image.secureUrl,
            publicId: image.publicId,
            assignmentId: assignment.id,
            campaignName: assignment.campaignName,
            taskName: assignment.taskName,
            eventId: assignment.packEvidence.eventId,
            timestamp: assignment.packEvidence.timestamp || image.uploadedAt,
            source: 'packEvidence'
          });
        }
      });
      
      // Extract photos from mediaRequirements
      assignment.mediaRequirements?.forEach((media: any) => {
        media.photos?.forEach((photoUrl: string) => {
          if (photoUrl && !imageMap.has(photoUrl)) {
            imageMap.set(photoUrl, {
              secureUrl: photoUrl,
              assetUrl: photoUrl,
              assignmentId: assignment.id,
              campaignName: assignment.campaignName,
              taskName: assignment.taskName,
              mediaFormatName: media.formatName,
              source: 'mediaRequirements'
            });
          }
        });
      });
      
      // Extract photos from postingEvents
      assignment.postingEvents?.forEach((event: any) => {
        event.evidence?.photos?.forEach((photoUrl: string) => {
          if (photoUrl && !imageMap.has(photoUrl)) {
            imageMap.set(photoUrl, {
              secureUrl: photoUrl,
              assetUrl: photoUrl,
              assignmentId: assignment.id,
              campaignName: assignment.campaignName,
              taskName: assignment.taskName,
              eventId: event.eventId,
              timestamp: event.timestamp,
              source: 'postingEvents'
            });
          }
        });
      });
    });
    
    console.log('🖼️ Enhanced Proof Modal: Found', imageMap.size, 'unique proof images from gallery proxy + assignments');
    
    return Array.from(imageMap.values());
  }, [campaignImages, storeAssignments]);

  useEffect(() => {
    console.log('[ENHANCED_PROOF_MODAL] useEffect triggered:', { isOpen, storeId, cycleId, operatorId, assignmentId });
    if (isOpen && storeId && cycleId) {
      console.log('[ENHANCED_PROOF_MODAL] Conditions met, calling fetchProofDetails');
      fetchProofDetails();
    } else {
      console.log('[ENHANCED_PROOF_MODAL] Conditions NOT met:', { 
        isOpen, 
        storeId, 
        cycleId, 
        operatorId,
        reason: !isOpen ? 'not open' : !storeId ? 'no storeId' : !cycleId ? 'no cycleId' : 'unknown'
      });
    }
  }, [isOpen, storeId, cycleId, assignmentId]);

  const fetchProofDetails = async () => {
    try {
      setLoading(true);
      
      console.log('[ENHANCED_PROOF_MODAL] Fetching proof details:', { storeId, cycleId, operatorId, assignmentId });
      
      // Loader that pulls canonical posting events + evidence via live-status assignment endpoint.
      const loadAssignmentFromProofOfPosting = async () => {
        if (!assignmentId) return null;

        try {
          console.log('[ENHANCED_PROOF_MODAL] Loading assignment via live-status assignment endpoint', assignmentId);
          const response = await http(`/api/live-status/assignment/${assignmentId}`);

          if (!response.ok) {
            console.error('❌ live-status assignment fetch failed:', response.status, response.statusText);
            return null;
          }

          const data = await response.json();
          const assignment = data.assignment;
          if (!assignment) {
            console.warn('⚠️ live-status assignment payload missing assignment');
            return null;
          }

          const postingEvents = Array.isArray(assignment.postingEvents)
            ? assignment.postingEvents.map((event: any) => ({
                id: event.id,
                postedUnits: event.postedUnits,
                notes: event.notes,
                timestamp: event.timestamp,
                group_id: event.group_id ?? event.groupId, // normalize for flexible packs
                groupId: event.group_id ?? event.groupId,
                groupName: event.groupName,
              }))
            : [];

          const proofImages =
            Array.isArray(assignment.evidenceAssets) && assignment.evidenceAssets.length > 0
              ? assignment.evidenceAssets.map((img: any) => ({
                  id: img.id,
                  secureUrl: img.secureUrl,
                  assetUrl: img.secureUrl,
                  publicId: img.publicId,
                  assignmentId: assignment.id,
                  taskName: assignment.taskName,
                  campaignName: assignment.campaignName,
                  eventId: postingEvents[0]?.id,
                }))
              : [];

          const normalizedAssignment = {
            id: assignment.id,
            campaignId: assignment.campaignId,
            storeId: assignment.storeId,
            operatorId: assignment.operatorId,
            status: assignment.status,
            taskName: assignment.taskName || `Assignment ${assignment.id}`,
            campaignName: assignment.campaignName || `Campaign ${assignment.campaignId}`,
            storeName: assignment.storeName || `Store ${assignment.storeId}`,
            storeAddress: assignment.storeAddress,
            operatorName: assignment.operatorName,
            operatorUsername: assignment.operatorUsername,
            cycleName: assignment.cycleName,
            mediaRequirements: assignment.mediaRequirements || [],
            postingEvents,
            packEvidence: proofImages.length
              ? {
                  proofImages: proofImages.map((p: any) => ({
                    secureUrl: p.secureUrl,
                    assetUrl: p.assetUrl,
                    publicId: p.publicId,
                    uploadedAt: p.uploadedAt || p.timestamp || undefined,
                  })),
                }
              : assignment.packEvidence || null,
          };

          return { normalizedAssignment, proofImages };
        } catch (err) {
          console.error('❌ live-status assignment load error:', err);
          return null;
        }
      };

      const loadSingleAssignment = async () => {
        if (!assignmentId) return null;
        return loadAssignmentFromProofOfPosting();
      };

      // Fast path: if we have assignmentId, try direct assignment fetch first to avoid paginating the entire cycle
      if (assignmentId) {
        const loadedDirect = await loadSingleAssignment();
        if (loadedDirect) {
          const { normalizedAssignment, proofImages } = loadedDirect;
          setStoreAssignments([normalizedAssignment]);
          setStoreInfo({
            storeName: normalizedAssignment.storeName,
            storeAddress: normalizedAssignment.storeAddress,
            operatorName: normalizedAssignment.operatorName,
            operatorUsername: normalizedAssignment.operatorUsername,
            cycleName: normalizedAssignment.cycleName,
          });
          setCampaignImages([]); // avoid extra gallery fetch when scoped
          if (proofImages?.length) {
            setFormatEvidence([]); // will be filled by fetchFormatEvidence
          }
          await fetchFormatEvidence([normalizedAssignment]);
          setLoading(false);
          return;
        }
      }

      // If cycleId is missing, try the fallback path first
      if (!cycleId || cycleId === 0) {
        const loaded = await loadAssignmentFromProofOfPosting();
        if (loaded) {
          const { normalizedAssignment, proofImages } = loaded;
          setStoreAssignments([normalizedAssignment]);
          setStoreInfo({
            storeName: normalizedAssignment.storeName,
            storeAddress: normalizedAssignment.storeAddress,
            operatorName: normalizedAssignment.operatorName,
            operatorUsername: normalizedAssignment.operatorUsername,
            cycleName: normalizedAssignment.cycleName,
          });
          setCampaignImages([]); // avoid mixing gallery images without store/cycle context
          if (proofImages?.length) {
            setFormatEvidence([]); // will be filled by fetchFormatEvidence
          }
          await fetchFormatEvidence([normalizedAssignment]);
        } else {
          toast({
            title: 'Error Loading Evidence',
            description: `Missing cycle ID and unable to load assignment ${assignmentId ?? ''}.`,
            variant: 'destructive',
          });
        }
        return;
      }
      
      // Use Live API instead of locked cycle data to get correct campaign_tasks.id values
      // Fetch multiple pages to ensure we include the requested assignment (pagination default is 50)
      const pageSize = 200;
      const maxPages = 10; // safety guard
      let page = 1;
      let totalCount = Infinity;
      let storeSignaturesFromApi: any[] = [];
      const allAssignments: any[] = [];

      while (page <= maxPages && allAssignments.length < totalCount) {
        const url = `/api/live-status/assignments/${cycleId}?page=${page}&pageSize=${pageSize}&includeArchived=true`;
        console.log('[ENHANCED_PROOF_MODAL] Calling:', url);
        
        const response = await http(url);
        
        console.log('[ENHANCED_PROOF_MODAL] Response:', { 
          status: response.status, 
          statusText: response.statusText,
          ok: response.ok,
          url: response.url,
          page,
        });
        
        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          console.error('❌ Error fetching live assignment data:', {
            status: response.status,
            statusText: response.statusText,
            errorText: errorText.substring(0, 200)
          });
          toast({
            title: 'Error Loading Evidence',
            description: `Failed to fetch assignments (${response.status}). Please ensure you're logged in and try again.`,
            variant: 'destructive'
          });
          return;
        }

        const data = await response.json();
        if (page === 1) {
          // CRITICAL FIX: Extract store signatures from API response (per store-cycle, NOT per assignment)
          // Store signatures are in data.storeSignatures array, keyed by storeId + operatorId
          storeSignaturesFromApi = data.storeSignatures || [];
          console.log('📋 Store signatures from API:', storeSignaturesFromApi.length, 'signatures');
        }

        const pageAssignments = data.data ?? data.assignments ?? [];
        const pageTotalCount = typeof data.totalCount === 'number' ? data.totalCount : pageAssignments.length;
        totalCount = Number.isFinite(pageTotalCount) ? pageTotalCount : pageAssignments.length;

        allAssignments.push(...pageAssignments);

        // Break early if we already fetched the assignment we're looking for
        if (assignmentId && pageAssignments.some((assignment: any) => assignment.id === assignmentId)) {
          console.log('✅ Found target assignment in page', page);
          break;
        }

        if (pageAssignments.length === 0 || allAssignments.length >= totalCount) {
          break;
        }

        page += 1;
      }

      console.log('Enhanced proof modal data (from Live API):', {
        totalAssignments: allAssignments.length,
        totalCount,
        pagesFetched: page,
      });
      
      // Filter assignments. If assignmentId is provided, prefer that over store/operator filters to avoid mismatches.
      // Handle both new paginated format { data: [...] } and legacy format { assignments: [...] }
      let filteredAssignments: any[] = [];
      if (assignmentId) {
        filteredAssignments = allAssignments.filter((assignment: any) => assignment.id === assignmentId);
        console.log('[ENHANCED_PROOF_MODAL] Filter by assignmentId only', {
          assignmentId,
          found: filteredAssignments.length,
        });
      } else {
        filteredAssignments = allAssignments.filter((assignment: any) => {
          const matchesStore = assignment.storeId === storeId;
          const matchesOperator = !operatorId || assignment.userId === operatorId || assignment.operatorId === operatorId;
          // Only show assignments with evidence submitted OR operator_complete status
          // Include admin submissions that have been enriched to admin_complete status
          // Include operator_complete assignments even without visible evidence data
          const hasEvidence = assignment.mediaRequirements?.some((media: any) => media.delivered > 0) || 
                             assignment.postedUnits > 0 || 
                             assignment.hasEvidence ||
                             assignment.postingEvents?.length > 0 ||
                             assignment.status === 'admin_complete' ||
                             assignment.status === 'operator_complete';
          return matchesStore && matchesOperator && hasEvidence;
        }) || [];
      }
      
      // CRITICAL FIX: Find the store signature for THIS store from API response
      // Signature is per store-cycle, shared across all assignments
      const matchingSignature = storeSignaturesFromApi.find((sig: any) => 
        sig.storeId === storeId && 
        (!operatorId || sig.operatorId === operatorId)
      );
      
      if (matchingSignature) {
        console.log('✅ Found store signature from API:', matchingSignature.url?.substring?.(0, 60) || 'typed-name');
        setStoreSignature({
          url: matchingSignature.url || null,
          managerName: matchingSignature.storeManagerName || null,
          signoffMethod: matchingSignature.signoffMethod || null,
        });
      } else {
        console.log('📝 No store signature found in API response for store:', storeId);
        // Fallback: Check if any assignment has storeSignature property (from new API format)
        const assignmentWithSignature = filteredAssignments.find((a: any) => a.storeSignature?.url || a.storeSignature?.storeManagerName);
        if (assignmentWithSignature?.storeSignature) {
          console.log('✅ Found store signature from assignment:', assignmentWithSignature.storeSignature.url?.substring?.(0, 60) || 'typed-name');
          setStoreSignature({
            url: assignmentWithSignature.storeSignature.url || assignmentWithSignature.storeSignature.signatureUrl || null,
            managerName: assignmentWithSignature.storeSignature.storeManagerName || null,
            signoffMethod: assignmentWithSignature.storeSignature.signoffMethod || null,
          });
        } else {
          setStoreSignature(null);
        }
      }

      console.log(`🔍 Enhanced Proof Modal: Found ${filteredAssignments.length} assignments with evidence for store ${storeId}`, 
        filteredAssignments.map((a: any) => ({ id: a.id, status: a.status, hasMediaReqs: !!a.mediaRequirements?.length })));

      // If nothing matched but we have an assignmentId, fetch directly from proof-of-posting data
      if (filteredAssignments.length === 0 && assignmentId) {
        const loaded = await loadAssignmentFromProofOfPosting();
        if (loaded) {
          const { normalizedAssignment } = loaded;
          setStoreAssignments([normalizedAssignment]);
          await fetchFormatEvidence([normalizedAssignment]);
          return;
        }
      }

      // Enrich assignment with evidence if the filtered assignment lacks posting events or proof images
      const maybeAssignment = filteredAssignments[0];
      const needsEnrichment =
        maybeAssignment &&
        (!maybeAssignment.postingEvents || maybeAssignment.postingEvents.length === 0) &&
        (!maybeAssignment.packEvidence || !maybeAssignment.packEvidence.proofImages || maybeAssignment.packEvidence.proofImages.length === 0);

      const enrichAssignmentEvidence = async (assignment: any) => {
        try {
          console.log('[ENHANCED_PROOF_MODAL] Enriching assignment via live-status assignment endpoint', assignment.id);
          const loaded = await loadAssignmentFromProofOfPosting();
          if (!loaded) {
            return null;
          }
          const { normalizedAssignment } = loaded;
          return { assignment: normalizedAssignment, formatEvidence: null };
        } catch (err) {
          console.error('❌ Enrichment error:', err);
          return null;
        }
      };

      if (needsEnrichment && maybeAssignment) {
        const enriched = await enrichAssignmentEvidence(maybeAssignment);
        if (enriched) {
          const { assignment: enrichedAssignment } = enriched;
          setStoreAssignments([enrichedAssignment]);
          await fetchFormatEvidence([enrichedAssignment]);
          // Preserve store info from existing assignment context
          setStoreInfo((prev: any) => ({
            storeName: enrichedAssignment.storeName ?? prev?.storeName,
            storeAddress: enrichedAssignment.storeAddress ?? prev?.storeAddress,
            operatorName: enrichedAssignment.operatorName ?? prev?.operatorName,
            operatorUsername: enrichedAssignment.operatorUsername ?? prev?.operatorUsername,
            cycleName: enrichedAssignment.cycleName ?? prev?.cycleName,
          }));
          console.log('[ENHANCED_PROOF_MODAL] Enrichment applied, returning early');
          return;
        }
      }

      setStoreAssignments(filteredAssignments);
      
      // Extract store info from the first assignment
      if (filteredAssignments.length > 0) {
        const firstAssignment = filteredAssignments[0];
        setStoreInfo({
          storeName: firstAssignment.storeName,
          storeAddress: firstAssignment.storeAddress,
          operatorName: firstAssignment.operatorName,
          operatorUsername: firstAssignment.operatorUsername,
          cycleName: firstAssignment.cycleName
        });

        // Detect if this is a flexible pack campaign
        const isFlexible = firstAssignment.isFlexiblePack || firstAssignment.campaign?.is_flexible_pack;
        setIsFlexiblePack(isFlexible);
        
        // Extract selected group from posting events if flexible pack
        if (isFlexible && firstAssignment.postingEvents?.length > 0) {
          const event = firstAssignment.postingEvents[0];
          if (event.group_id !== undefined) {
            // Determine group name from acceptable_format_groups if available
            let groupName = `Group ${event.group_id}`;
            if (firstAssignment.acceptable_format_groups) {
              const group = firstAssignment.acceptable_format_groups.find((g: any) => g.groupId === event.group_id);
              if (group) {
                groupName = group.groupName;
              }
            }
            setSelectedGroup({
              groupId: event.group_id,
              groupName: groupName
            });
          }
        }
        
        console.log('🔍 Flexible Pack Detection:', {
          isFlexible,
          campaignId: firstAssignment.campaignId,
          campaignName: firstAssignment.campaignName,
          selectedGroup: selectedGroup,
          hasPostingEvents: firstAssignment.postingEvents?.length > 0,
          firstEventGroupId: firstAssignment.postingEvents?.[0]?.group_id
        });
      }
      
      // NOTE: Store signature is now extracted from API response above (storeSignaturesFromApi)
      // The old logic that searched through posting events' evidence has been removed
      // Signatures are per store-cycle, NOT per campaign or posting event
      
      // Fetch format evidence for all assignments
      await fetchFormatEvidence(filteredAssignments);
      
    } catch (error) {
      console.error('Error fetching proof details:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch proof details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFormatEvidence = async (assignments: any[]) => {
    try {
      const formatEvidenceData: FormatEvidenceDetail[] = [];
      
      // For flexible packs, we need to filter formats by selected group
      const isFlexible = assignments.length > 0 && (assignments[0].isFlexiblePack || assignments[0].campaign?.is_flexible_pack);
      let selectedGroupId: number | null = null;
      
      if (isFlexible && assignments[0].postingEvents?.length > 0) {
        selectedGroupId = assignments[0].postingEvents[0].group_id;
      }
      
      console.log('🔍 Fetching format evidence:', {
        isFlexible,
        selectedGroupId,
        assignmentsCount: assignments.length
      });
      
      // Collect all posting events and their format evidence
      const eventFetchPromises: Promise<void>[] = [];

      for (const assignment of assignments) {
        if (assignment.postingEvents) {
          for (const event of assignment.postingEvents) {
            // For flexible packs, only process events from the selected group
            if (isFlexible && selectedGroupId !== null && event.group_id !== selectedGroupId) {
              console.log(`⏭️ Skipping event ${event.id} - wrong group (${event.group_id} vs ${selectedGroupId})`);
              continue;
            }

            eventFetchPromises.push((async () => {
              try {
                // Fetch format evidence for each posting event
                const response = await http(`/api/format-evidence/posting-event/${event.id}/formats`);
                
                if (response.ok) {
                  const formatData = await response.json();
                  
                  // Process format evidence data
                  if (formatData.formats && Array.isArray(formatData.formats)) {
                    console.log(`✅ Format evidence loaded for event ${event.id}:`, formatData.formats.length, 'formats');
                    formatData.formats.forEach((format: any) => {
                      const status = format.actualCount === undefined ? 'Not Installed' :
                                    format.actualCount === format.expectedCount ? 'Installed' : 'Partial';
                      
                      formatEvidenceData.push({
                        formatId: format.formatId,
                        formatName: format.formatName,
                        expectedCount: format.expectedCount,
                        actualCount: format.actualCount,
                        status,
                        discrepancyReason: format.discrepancyReason,
                        completedBy: format.completedBy,
                        postingEventId: event.id,
                        proofImageUrl: assignment.packEvidence?.proofImages?.[0]?.secureUrl
                      });
                    });
                  } else {
                    console.warn('Format evidence response missing formats array for event:', event.id, formatData);
                  }
                } else {
                  console.error(`❌ Failed to fetch format evidence for event ${event.id}: ${response.status} ${response.statusText}`);
                  toast({
                    title: 'Evidence Loading Issue',
                    description: `Unable to load format details for task ${event.id}. Some evidence may not display.`,
                    variant: 'destructive'
                  });
                }
              } catch (error) {
                console.error('❌ Error fetching format evidence for event:', event.id, error);
                toast({
                  title: 'Evidence Load Error',
                  description: `Failed to retrieve format evidence. Please refresh and try again.`,
                  variant: 'destructive'
                });
              }
            })());
          }
        }
        
        // For assignments without format evidence, create default entries from media requirements
        if (!assignment.postingEvents || assignment.postingEvents.length === 0) {
          assignment.mediaRequirements?.forEach((media: any) => {
            formatEvidenceData.push({
              formatId: media.formatId || 0,
              formatName: media.name || media.formatName || 'Unknown Format',
              expectedCount: media.quantity || 1,
              actualCount: undefined,
              status: 'Not Installed',
              discrepancyReason: undefined,
              completedBy: undefined,
              postingEventId: undefined,
              proofImageUrl: undefined
            });
          });
        }
      }
      
      await Promise.all(eventFetchPromises);

      setFormatEvidence(formatEvidenceData);
      console.log('📊 Format Evidence loaded:', formatEvidenceData.length, 'format entries');
      
    } catch (error) {
      console.error('Error fetching format evidence:', error);
      setFormatEvidence([]);
    }
  };

  /**
   * @deprecated DEPRECATED - Admin approval/rejection has moved to Tracker Review Tab
   * 
   * All approvals and rejections should now be done via:
   * /admin/activation-cockpit/review?retailerId={id}&cycleId={id}
   * 
   * This uses the /api/review/decisions endpoint which:
   * - Creates proper review_decisions records
   * - Supports REPOST task creation for rejections
   * - Sends push notifications to operators
   * - Maintains proper audit trail
   * 
   * This function is kept for reference but should not be used.
   */
  const handleAdminApproval = async (_assignmentId: number, _action: 'approve' | 'reject', _reason?: string) => {
    toast({
      title: 'Action Moved',
      description: 'Please use the Tracker Review page (Cockpit → Review) to approve or reject submissions.',
      variant: 'destructive',
    });
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Loading Proof Details...</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">Loading...</div>
        </DialogContent>
      </Dialog>
    );
  }



  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Proof of Posting - {storeInfo?.storeName || 'Store Details'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Store & Operator Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Store & Operator Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid gap-4 ${SHOW_ASSIGNMENT_ID ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'}`}>
                <div>
                  <Label className="font-semibold">Store</Label>
                  <p>{storeInfo?.storeName || 'N/A'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Operator</Label>
                  <p>{storeInfo?.operatorName || 'N/A'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Cycle</Label>
                  <p>{storeInfo?.cycleName || 'N/A'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Address</Label>
                  <p>{storeInfo?.storeAddress || 'N/A'}</p>
                </div>
                {SHOW_ASSIGNMENT_ID && storeAssignments.length > 0 && (
                  <div>
                    <Label className="font-semibold">Assignment ID</Label>
                    <Badge variant="outline" className="font-mono text-xs">
                      {storeAssignments[0]?.id || 'N/A'}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Flexible Pack or Standard Assignment Summary */}
          {isFlexiblePack ? (
            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-orange-600" />
                  Selected Group
                </CardTitle>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
                  <p className="text-sm font-medium text-orange-800">
                    ⚠️ Flexible Pack: Operator selected one format group in the field
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {selectedGroup ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        Group {selectedGroup.groupId}
                      </Badge>
                      <span className="font-semibold text-lg">{selectedGroup.groupName}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      This is the format group that was installed by the operator during their store visit.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No group has been selected for this store. Awaiting operator submission.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Assignment Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-semibold">
                      {assignmentId ? 'Assignment' : `Total Assignments`}
                    </Label>
                    <p className="text-2xl font-bold">
                      {assignmentId ? `ID ${assignmentId}` : storeAssignments.length}
                    </p>
                  </div>
                  <div>
                    <Label className="font-semibold">With Evidence</Label>
                    <p className="text-2xl font-bold text-green-600">
                      {storeAssignments.filter(assignment => 
                        assignment.mediaRequirements?.some((media: any) => media.delivered > 0)
                      ).length}
                    </p>
                  </div>
                  <div>
                    <Label className="font-semibold">Pending</Label>
                    <p className="text-2xl font-bold text-orange-600">
                      {storeAssignments.filter(assignment => 
                        !assignment.mediaRequirements?.some((media: any) => media.delivered > 0)
                      ).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Store Signature Section */}
          {storeSignature && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PenTool className="h-4 w-4" />
                  Store Manager Signature
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">
                    Operator signature captured during store visit completion covering all pack tasks in this store.
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {storeSignature.url ? (
                      <img 
                        src={storeSignature.url} 
                        alt="Store Manager Signature" 
                        className="max-w-full h-auto max-h-32 mx-auto"
                        style={{ backgroundColor: 'white' }}
                      />
                    ) : (
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Typed manager sign-off</p>
                        <p className="mt-2 font-semibold text-gray-900">{storeSignature.managerName || 'Manager name unavailable'}</p>
                      </div>
                    )}
                  </div>
                  {storeSignature.managerName && storeSignature.url && (
                    <div className="text-sm text-gray-600">Signed by {storeSignature.managerName}</div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Enhanced Layout: Side-by-side on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* LEFT SIDE: Proof Images Grid (2/3 width on desktop) */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileImage className="h-4 w-4" />
                    Proof Images ({allProofImages.length} photos)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allProofImages.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {allProofImages.map((image: any, idx: number) => (
                        <div key={idx} className="space-y-2">
                          <div 
                            className={`aspect-square w-full max-w-[150px] mx-auto border-2 border-gray-200 rounded-lg overflow-hidden transition-colors ${image.secureUrl ? 'cursor-pointer hover:border-primary' : ''}`}
                            onClick={() => image.secureUrl && window.open(image.secureUrl, '_blank')}
                            title={image.secureUrl ? 'Click to enlarge' : 'Image temporarily unavailable'}
                          >
                            <SafeImage
                              src={image.secureUrl}
                              alt="Proof of posting"
                              className="w-full h-full object-cover"
                              placeholderType="photo"
                              helperText="Image temporarily unavailable"
                              onImageError={() => {
                                console.error('[IMAGE_ERROR] Failed to load image:', {
                                  url: image.secureUrl,
                                  assetUrl: image.assetUrl,
                                  publicId: image.publicId,
                                  eventId: image.eventId
                                });
                              }}
                            />
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-medium text-gray-700">{image.campaignName}</p>
                            <p className="text-xs text-gray-500">{image.taskName || 'Task Evidence'}</p>
                            {image.assignmentId && (
                              <p className="text-xs text-muted-foreground">Assignment #{image.assignmentId}</p>
                            )}
                            {image.mediaFormatName && (
                              <p className="text-xs text-blue-500">{image.mediaFormatName}</p>
                            )}
                            {image.eventId && (
                              <p className="text-xs text-green-500">Event #{image.eventId}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileImage className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No proof images available for this store visit.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* RIGHT SIDE: Store Signature & Metadata (1/3 width on desktop) */}
            <div className="lg:col-span-1">

            </div>
          </div>

          {/* Media Formats Reference Section */}
          {storeAssignments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSignature className="h-4 w-4" />
                  Media Formats (Reference)
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Media formats included in each pack for operator review reference
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Group assignments by pack/task name */}
                  {Array.from(new Set(storeAssignments.map(a => a.taskName))).map((taskName) => {
                    const taskAssignments = storeAssignments.filter(a => a.taskName === taskName);
                    const firstAssignment = taskAssignments[0];
                    
                    return (
                      <div key={taskName} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center gap-2 mb-3">
                          <Package className="h-4 w-4 text-blue-600" />
                          <h4 className="font-semibold text-gray-900">{taskName}</h4>
                          <Badge variant="outline" className="text-xs">
                            {firstAssignment.campaignName}
                          </Badge>
                        </div>
                        
                        {/* Display media formats for this pack */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {firstAssignment.mediaRequirements?.map((media: any, idx: number) => (
                            <div key={`${taskName}-media-${idx}`} className="flex items-center gap-2 p-2 bg-white rounded border">
                              <div className="flex-1">
                                <p className="font-medium text-sm text-gray-900">{media.name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="secondary" className="text-xs">
                                    {media.type}
                                  </Badge>
                                  <span className="text-xs text-gray-600">
                                    {media.quantity} {media.quantity === 1 ? 'unit' : 'units'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))} 
                        </div>
                        
                        {(!firstAssignment.mediaRequirements || firstAssignment.mediaRequirements.length === 0) && (
                          <div className="text-sm text-gray-500 italic">
                            No media format requirements defined for this pack
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Format-Level Installation Status Table - Flexible or Standard */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                {isFlexiblePack && selectedGroup ? 
                  `${selectedGroup.groupName} - Format Details` : 
                  'Format-Level Installation Status'
                }
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {isFlexiblePack ? 
                  'Formats from the selected group with planned vs posted quantities and discrepancy details' :
                  'Detailed status and discrepancy information for each media format'
                }
              </p>
            </CardHeader>
            <CardContent>
              {formatEvidence.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Format Name</TableHead>
                      <TableHead>Planned</TableHead>
                      <TableHead>Posted</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Discrepancy</TableHead>
                      <TableHead>Reason & Timestamp</TableHead>
                      <TableHead>Photo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formatEvidence.map((format, index) => {
                      const discrepancy = format.expectedCount - (format.actualCount || 0);
                      return (
                        <TableRow key={`${format.formatId}-${index}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{format.formatName}</p>
                              <p className="text-xs text-muted-foreground">ID: {format.formatId}</p>
                              {format.completedBy && (
                                <Badge variant="outline" className="text-xs mt-1">
                                  {format.completedBy === 'admin' ? '🛡️ Admin' : '👤 Operator'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              {format.expectedCount}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={
                                format.actualCount === undefined ? 'bg-gray-50 text-gray-600' :
                                format.actualCount === format.expectedCount ? 'bg-green-50 text-green-700' :
                                'bg-amber-50 text-amber-700'
                              }
                            >
                              {format.actualCount ?? 'TBD'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                format.status === 'Installed' ? 'default' :
                                format.status === 'Partial' ? 'secondary' : 
                                'destructive'
                              }
                              className={
                                format.status === 'Installed' ? 'bg-green-100 text-green-800' :
                                format.status === 'Partial' ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }
                            >
                              {format.status === 'Installed' ? '✅ Installed' :
                               format.status === 'Partial' ? '⚠️ Partial' :
                               '❌ Not Installed'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {discrepancy !== 0 ? (
                              <Badge 
                                variant="outline" 
                                className={discrepancy > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}
                              >
                                {discrepancy > 0 ? `-${discrepancy}` : `+${Math.abs(discrepancy)}`}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50 text-gray-600">
                                0
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="max-w-xs">
                              {format.discrepancyReason ? (
                                <div className="space-y-1">
                                  <p className="text-sm text-gray-700">{format.discrepancyReason}</p>
                                  {format.postingEventId && (
                                    <p className="text-xs text-muted-foreground">Event #{format.postingEventId}</p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400 italic">No reason provided</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {format.proofImageUrl ? (
                              <div 
                                className="w-8 h-8 cursor-pointer border border-gray-200 rounded overflow-hidden hover:border-primary"
                                onClick={() => window.open(format.proofImageUrl, '_blank')}
                                title="Click to enlarge"
                              >
                                <img
                                  src={format.proofImageUrl}
                                  alt="Proof"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    console.error('[IMAGE_ERROR] Failed to load format proof image:', format.proofImageUrl);
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                  onLoad={() => {
                                    console.log('[IMAGE_SUCCESS] Format proof image loaded:', format.proofImageUrl);
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">No photo</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No format evidence data available for this store.</p>
                  <p className="text-sm">Check if format-level tracking is enabled for these campaigns.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Photo & Signature Evidence Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileImage className="h-4 w-4" />
                Photo & Signature Evidence
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                All photographic and signature evidence grouped by pack submission
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Group by pack/task */}
                {Array.from(new Set(storeAssignments.map(a => a.taskName))).map((taskName) => {
                  const taskAssignments = storeAssignments.filter(a => a.taskName === taskName);
                  const taskImages = allProofImages.filter(img => 
                    taskAssignments.some(assignment => assignment.id === img.assignmentId)
                  );
                  
                  return (
                    <div key={taskName} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Package className="h-4 w-4 text-blue-600" />
                        <h4 className="font-semibold">{taskName}</h4>
                        <Badge variant="outline" className="text-xs">
                          {taskImages.length} photos
                        </Badge>
                      </div>
                      
                      {taskImages.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {taskImages.map((image: any, idx: number) => (
                            <div key={idx} className="space-y-2">
                              <div 
                                className={`aspect-square w-full border-2 border-gray-200 rounded-lg overflow-hidden transition-colors ${image.secureUrl ? 'cursor-pointer hover:border-primary' : ''}`}
                                onClick={() => image.secureUrl && window.open(image.secureUrl, '_blank')}
                                title={image.secureUrl ? 'Click to enlarge' : 'Image temporarily unavailable'}
                              >
                                <SafeImage
                                  src={image.secureUrl}
                                  alt="Evidence"
                                  className="w-full h-full object-cover"
                                  placeholderType="photo"
                                  helperText="Image temporarily unavailable"
                                />
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-gray-600">{image.campaignName}</p>
                                {image.eventId && (
                                  <p className="text-xs text-green-600">Event #{image.eventId}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <FileImage className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No photos available for this pack</p>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Show signature if available */}
                {storeSignature && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <PenTool className="h-4 w-4 text-purple-600" />
                      <h4 className="font-semibold">Store Manager Signature</h4>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      {storeSignature.url ? (
                        <img 
                          src={storeSignature.url} 
                          alt="Store Manager Signature" 
                          className="max-w-full h-auto max-h-24 mx-auto"
                          style={{ backgroundColor: 'white' }}
                        />
                      ) : (
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Typed manager sign-off</p>
                          <p className="mt-2 font-semibold text-gray-900">{storeSignature.managerName || 'Manager name unavailable'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {storeAssignments.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileImage className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No evidence submissions found for this store.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Admin Actions */}
          {storeAssignments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Admin Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Review the format-level installation status and evidence above, then approve or reject each pack submission:
                  </p>
                  <div className="grid gap-3">
                    {storeAssignments.map((assignment) => (
                      <div key={assignment.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{assignment.campaignName}</p>
                          <p className="text-sm text-muted-foreground">{assignment.taskName}</p>
                          <Badge 
                            variant={
                              assignment.status === 'admin_approved' ? 'default' :
                              assignment.status === 'admin_complete' ? 'default' :
                              assignment.status === 'admin_rejected' ? 'destructive' : 'secondary'
                            }
                            className="text-xs mt-1"
                          >
                            {assignment.status === 'admin_approved' ? '✅ Approved' :
                             assignment.status === 'admin_complete' ? '✅ Complete' :
                             assignment.status === 'admin_rejected' ? '❌ Rejected' :
                             '⏳ Pending Review'}
                          </Badge>
                        </div>
                        {/* DEPRECATED: Approval buttons removed - use Tracker Review Tab instead */}
                        <div className="text-xs text-muted-foreground italic">
                          Review via Cockpit → Review
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
